import React from 'react';
import '../../App.css';
import './visualisation.css';
import '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// icons

// classes
import Scrollbox from '../../tools/scrollbox';
import SidePanel from '../../tools/side-panel';
import ComputeSession from '../compute-tiles/compute-session';
import PleaseWait from '../../tools/please-wait';
import EditableSelect from '../../tools/editable-select';

// types
import { CanfarServiceType, Site } from '../../utils/types';

// enum
import { SidePanelPosition } from '../../tools/side-panel';

// import styles.
import {
  customStylesDark,
  customStylesLight,
} from '../../utils/custom-styles-select-box';

// functions
import { siteName } from '../../utils/functions';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import { setFilterMaximised } from './interactive-sessions-slice';
import { rG_setSite } from '../../app/global-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S / M A I N   F U N C T I O N   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function InteractiveSessions(props: { type: string }) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // filter box maximised, or not?
  const sFilterMaximised: boolean[] = useAppSelector(
    (state) => state.interactiveSessions.filterMaximised,
  );

  // keep a list of visible tools. these are running sessions, available services, and web resources.
  const sSessions = useAppSelector((state) => state.interactiveSessions.sessions);

  // sessions-loading flags.
  const sSessionsLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[] = useAppSelector((state) => state.interactiveSessions.sessionsLoading);

  // global state.
  const sG_site: Site | undefined = useAppSelector((state) => state.global.site);
  const sG_selectedCanfar: CanfarServiceType | undefined = useAppSelector(
    (state) => state.global.selectedCanfar,
  );
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // data state.
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);

  //	-------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	-------------------------------------------------

  // multi-language support
  const { i18n, t } = useTranslation();

  const ARRAY_INDEX: number =
    props.type === 'visualisation' ? 0 : props.type === 'notebooks' ? 1 : 2;

  // extract the list of active canfar sessions that are loading.
  const activeSessionsCanfarLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[] = sSessionsLoading.filter(
    (item) => item.resource_type === 'canfar' && item.compute_status === 'running',
  );

  // extract the list of available canfar sessions that are loading.
  const availableSessionsCanfarLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[] = sSessionsLoading.filter(
    (item) => item.resource_type === 'canfar' && item.compute_status === 'available',
  );

  // determine if the active SRCNet sessions are loading.
  const activeSessionsSRCNetLoading: boolean =
    sSessionsLoading.findIndex(
      (item) => item.resource_type === 'srcnet' && item.compute_status === 'running',
    ) > -1;

  // create a list of site option, based upon the list of sites.
  const _siteOptions: {
    value: string;
    label: string;
    icon?: string;
  }[] = sD_sites.map((site) => ({
    value: site.site_uuid ?? '',
    label: siteName({ site: site }),
    icon: site.icon /*site.icon === undefined ? '' : site.icon,*/,
  }));

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	H O O K S
  //
  //	-------------------------------------------------

  //	-------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	-------------------------------------------------

  return (
    <div
      style={{
        flex: '0 0 auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        padding: '0px 5px 0px 0px',
      }}
    >
      <div
        style={{
          flex: '0 0 auto',
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* the column containing filter boxes, side panels, etc. */}
        <Scrollbox
          direction="vertical"
          width="100%"
          flex="1 1"
          flexDirection="column"
          padding="0px 5px 10px 0px"
        >
          <SidePanel
            position={SidePanelPosition.LEFT}
            title={t('Side panel')}
            maximised={sFilterMaximised[ARRAY_INDEX]}
            setMaximised={setFilterMaximised}
            redux={true}
            index={ARRAY_INDEX}
            width={'auto'}
          >
            <label
              id="interactive-sessions-active-label"
              className="text-colour"
              style={{ flex: '0 0 auto', margin: '0px 10px 0px 10px' }}
            >
              Active sessions:
            </label>

            <div style={{ flex: '0 0 15px' }} />
            <div
              role="navigation"
              aria-labelledby="interactive-sessions-active-label"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              {(activeSessionsCanfarLoading.findIndex(
                (item) => item.id === sG_selectedCanfar?.id,
              ) > -1 ||
                activeSessionsSRCNetLoading) && (
                <div style={{ marginLeft: '10px', marginBottom: '10px' }}>
                  <PleaseWait text="Loading....." visible={true} />
                </div>
              )}
              {sSessions
                .filter(
                  (item) =>
                    (item.resource_type === 'canfar' ||
                      item.resource_type === 'srcnet') &&
                    (item.compute_status === 'running' ||
                      item.compute_status === 'pending' ||
                      item.overridden_status === 'starting') &&
                    ((item.session_type === 'carta' && props.type === 'visualisation') ||
                      (item.session_type === 'jupyterhub' &&
                        props.type === 'notebooks') ||
                      (item.session_type === 'notebook' && props.type === 'notebooks') ||
                      (item.session_type === 'desktop' &&
                        props.type === 'virtual-desktops')) /*&&
                    item.site.site_uuid === (sG_site?.site_uuid ?? '')*/,
                )
                .map((item, index) => (
                  <div style={{ margin: '5px 0px 5px 0px' }}>
                    <ComputeSession key={index} session={item} />
                  </div>
                ))}
            </div>
            <hr style={{ border: '1pt solid gray' }} />
            <label
              id="interactive-sessions-new-session-label"
              className="text-colour"
              style={{
                flex: '0 0 auto',
                margin: '0px 10px 0px 10px',
              }}
            >
              Launch new session:
            </label>
            <div style={{ flex: '0 0 15px' }} />
            <label
              id="interactive-sessions-site"
              className="text-colour"
              style={{
                margin: '0px 0px 5px 10px',
              }}
            >
              Site (skaha):
            </label>
            <div
              style={{
                flex: '0 0 auto',
                margin: '0px 10px 0px 10px',
                minWidth: '277px',
                width: 'auto',
              }}
            >
              <EditableSelect
                aria-labelledby="interactive-sessions-site"
                gatewayTheme={sG_darkMode ? 'dark' : 'light'}
                styles={sG_darkMode ? customStylesDark : customStylesLight}
                isDisabled={false}
                isSearchable={false}
                options={_siteOptions}
                placeholder={t('Site')}
                value={_siteOptions.find((site) => site.value === sG_site?.site_uuid)}
                onChange={(
                  selectedOption: {
                    value: string | number;
                    label: string;
                    icon?: string;
                  } | null,
                ) =>
                  dispatch(
                    rG_setSite(
                      sD_sites.find((find) => find.site_uuid === selectedOption?.value) ??
                        {},
                    ),
                  )
                }
                isClearable={false}
                showSiteIcon={true}
              />
            </div>
            <div
              role="navigation"
              aria-labelledby="interactive-sessions-new-session-label"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              {availableSessionsCanfarLoading.findIndex(
                (item) => item.id === sG_selectedCanfar?.id,
              ) > -1 && (
                <div style={{ marginLeft: '10px', marginBottom: '10px' }}>
                  <PleaseWait text="Loading....." visible={true} />
                </div>
              )}
              {sSessions
                .filter(
                  (item) =>
                    (item.resource_type === 'canfar' ||
                      item.resource_type === 'srcnet') &&
                    item.compute_status === 'available' &&
                    ((item.session_type === 'carta' && props.type === 'visualisation') ||
                      (item.session_type === 'jupyterhub' &&
                        props.type === 'notebooks') ||
                      (item.session_type === 'notebook' && props.type === 'notebooks') ||
                      (item.session_type === 'desktop' &&
                        props.type === 'virtual-desktops')) &&
                    item.site.site_uuid === (sG_site?.site_uuid ?? ''),
                )
                .map((item, index) => (
                  <div style={{ margin: '5px 0px 5px 0px' }}>
                    <ComputeSession key={index} session={item} />
                  </div>
                ))}
            </div>
            {ARRAY_INDEX === 0 && <hr style={{ border: '1pt solid gray' }} />}
            {ARRAY_INDEX === 0 && (
              <label
                id="interactive-sessions-web-resources-label"
                className="text-colour"
                style={{ flex: '0 0 auto', margin: '0px 10px 0px 10px' }}
              >
                Web resources:
              </label>
            )}
            <div
              role="navigation"
              aria-labelledby="interactive-sessions-web-resources-label"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              {ARRAY_INDEX === 0 && <div style={{ flex: '0 0 10px' }} />}
              {ARRAY_INDEX === 0 &&
                sSessions
                  .filter(
                    (item) =>
                      item.resource_type === 'www' && props.type === 'visualisation',
                  )
                  .map((item, index) => <ComputeSession key={index} session={item} />)}
            </div>
            <div style={{ flex: '0 0 10px' }} />
          </SidePanel>
        </Scrollbox>
      </div>
    </div>
  );
  //
} // InteractiveSessions
